import { AuxiliumComponent } from "../auxilium-component";

export default class SortableList extends AuxiliumComponent {
	static observable = ["list-class", "listitem-class", "list-style"];
	static observableBooleans = ["ordered"];

	constructor() {
		super();

		this.Options = {
			Animation: {
				Speed: 200,
				Easing: "ease-out"
			}
		};

		this.classList.add("sortable-list__wrapper");
	}

	parsedCallback() {
		const list = document.createElementWithContents(this.ordered ? "ol" : "ul", "", {
			className: this.listClass,
			ontouchstart: ev => this.#DragStart(ev),
			onmousedown: ev => this.#DragStart(ev)
		});

		if (this.identifier) { list.id = this.identifier; }
		if (this.listStyle) { list.classList.add(`list-${this.listStyle}`); }

		list.classList.add("sortable-list");

		for (const item of this.querySelectorAll(":scope > chip-sortablelistitem")) {
			this.AddItem(item, list);
		}

		this.appendChild(list);
	}

	AddItem(item, list) {
		const listItem = document.createElementWithContents("li", item),
			itemID = item.getAttribute("identifier");

		if (itemID) { listItem.id = itemID; }
		if (this.listitemClass) { listItem.classList.add(this.listitemClass); }

		(list ?? this.querySelector("ul,ol")).appendChild(listItem);
	}

	#GetDragY(event) { return event.touches ? (event.touches[0] || event.changedTouches[0]).pageY : event.pageY; }

	#Transition(property) { return `${property} ${this.Options.Animation.Speed}ms ${this.Options.Animation.Easing}`; }

	#ClearStyles(element, ...properties) {
		for (const property of properties) { element.style.removeProperty(property); }

		if (!element.style.cssText.trim()) { element.removeAttribute("style"); }
	}

	#DragStart(event) {
		if (this.Animation || this.Drag) { return; }
		if (event.type === "mousedown" && event.which !== 1) { return; }
		if (event.type === "touchstart" && event.touches.length > 1) { return; }

		const list = this.querySelector("ul,ol"),
			listItem = event.target.closest(".sortable-list > li");

		if (!event.target.closest(".sortable-handle") || !listItem || !list?.contains(listItem)) { return; }

		const items = [...list.querySelectorAll(":scope > li")],
			bounds = items.map(item => item.getBoundingClientRect()),
			offsets = bounds.map(bound => bound.top - bounds[0].top),
			heights = bounds.map(bound => bound.height),
			index = items.indexOf(listItem);

		this.Drag = {
			Item: listItem,
			Items: items,
			Offsets: offsets,
			Heights: heights,
			Spacing: items.length > 1 ? offsets[1] - heights[0] : 0,
			Index: index,
			Order: items.map((_, i) => i),
			Position: index,
			StartY: this.#GetDragY(event),
			Touch: event.type === "touchstart"
		};

		Object.assign(listItem.style, {
			position: "relative",
			zIndex: 2
		});

		listItem.classList.add("is-dragging");

		Object.assign(document.body.style, {
			cursor: "grabbing",
			userSelect: "none"
		});

		this.#ToggleDragHandlers(true);
	}

	#DragMove(event) {
		if (!this.Drag) { return; }

		const offset = this.#GetOffset(this.#GetDragY(event)),
			position = this.#GetPosition(offset);

		this.Drag.Item.style.transform = `translateY(${offset}px)`;

		if (position !== this.Drag.Position) {
			this.Drag.Position = position;
			this.Drag.Order = this.#GetOrder(position);

			this.#Reposition();
		}

		event.preventDefault();

		event.target.closest?.("chip-sortablelistitem")?.raiseEvent("drag");
	}

	#DragEnd() {
		if (!this.Drag) { return; }

		const drag = this.Drag,
			list = this.querySelector("ul,ol"),
			starts = drag.Items.map(item => item.getBoundingClientRect().top);

		this.Drag = null;
		this.Animation = true;

		this.#ToggleDragHandlers(false);

		document.body.style.removeProperty("cursor");
		document.body.style.removeProperty("user-select");

		drag.Item.removeClass("is-dragging");

		for (const item of drag.Items) {
			this.#ClearStyles(item, "transform", "transition");
		}

		if (drag.Position !== drag.Index) {
			list.append(...drag.Order.map(index => drag.Items[index]));
		}

		const ends = drag.Items.map(item => item.getBoundingClientRect().top);

		drag.Items.forEach((item, index) => {
			if (Math.abs(starts[index] - ends[index]) < 0.5) { return; }

			item.style.transform = `translateY(${starts[index] - ends[index]}px)`;
		});

		void list.offsetHeight;

		for (const item of drag.Items) {
			if (!item.style.transform) { continue; }

			Object.assign(item.style, {
				transition: this.#Transition("transform"),
				transform: ""
			});
		}

		setTimeout(() => {
			for (const item of drag.Items) {
				this.#ClearStyles(item, "position", "transform", "transition", "z-index");
			}

			this.Animation = false;

			drag.Item.querySelector("chip-sortablelistitem")?.raiseEvent("dragend");
			this.raiseEvent("change");
		}, this.Options.Animation.Speed);
	}

	/**
	 * Keeps the dragged item within the bounds of the list, so that it can't be pulled out of
	 * the area the other items are able to fill.
	 * @param {number} y The current pointer position
	 * @return {number} How far the item is to be offset from where it started
	 */
	#GetOffset(y) {
		const { Offsets, Heights, Index, StartY } = this.Drag,
			furthest = Math.max(0, Offsets.at(-1) + Heights.at(-1) - Heights[Index]);

		return Math.min(Math.max(Offsets[Index] + y - StartY, 0), furthest) - Offsets[Index];
	}

	/**
	 * Finds the slot the dragged item is nearest to, measured from the middle of each so that an
	 * item claims a slot as soon as it has covered half of it. The slots are the ones the list
	 * started out with, which keeps the answer settled while the items themselves are moving.
	 * @param {number} offset How far the item has been dragged
	 * @return {number} The index the item is to sit at
	 */
	#GetPosition(offset) {
		const { Offsets, Heights, Index } = this.Drag,
			middle = Offsets[Index] + offset + (Heights[Index] / 2);

		let position = Index,
			nearest = Infinity;

		for (let index = 0; index < Offsets.length; index++) {
			const distance = Math.abs(middle - (Offsets[index] + (Heights[index] / 2)));

			if (distance >= nearest) { continue; }

			nearest = distance;
			position = index;
		}

		return position;
	}

	#GetOrder(position) {
		const order = this.Drag.Items.map((_, index) => index);

		order.splice(position, 0, ...order.splice(this.Drag.Index, 1));

		return order;
	}

	#Reposition() {
		const { Items, Offsets, Heights, Spacing, Order, Index } = this.Drag,
			slots = [];

		let top = 0;

		for (const index of Order) {
			slots[index] = top;
			top += Heights[index] + Spacing;
		}

		Items.forEach((item, index) => {
			if (index === Index) { return; }

			const shift = slots[index] - Offsets[index];

			Object.assign(item.style, {
				transition: this.#Transition("transform"),
				transform: Math.abs(shift) < 0.5 ? "" : `translateY(${shift}px)`
			});
		});
	}

	#ToggleDragHandlers(bind) {
		this.DragHandlers ??= {
			Move: ev => this.#DragMove(ev),
			End: () => this.#DragEnd()
		};

		const { Move, End } = this.DragHandlers;

		if (bind) {
			if (this.Drag.Touch) {
				// Moving has to be told it isn't passive, otherwise it can't stop the page scrolling
				window.addEventListener("touchmove", Move, { passive: false });
				window.addEventListener("touchend", End);
				window.addEventListener("touchcancel", End);
			} else {
				window.addEventListener("mousemove", Move);
				window.addEventListener("mouseup", End);
			}

			return;
		}

		window.removeEventListener("touchmove", Move);
		window.removeEventListener("touchend", End);
		window.removeEventListener("touchcancel", End);
		window.removeEventListener("mousemove", Move);
		window.removeEventListener("mouseup", End);
	}
}
